import {
  AppUiRecordDoc,
  AppUiRecordSaveInput,
  AppUiRecordWrite,
  AppUiRefusal,
  AppUiUploadOutcome,
  AppUiUploadRequest,
} from '@omni/interfaces/app-ui/record.interface';
import {
  DB_KEY_PREFIXES,
  OFFLINE_DATA_COUNT_ENTITY_NAME,
  PREFIX_SEARCH_ENDKEY_UNICODE,
} from '@omni/config/pouch-db.config';
import {
  DeltaService,
  EntityNames,
} from '@omni/data-services/delta/delta.service';
import {
  OfflineServiceBase,
  OfflineWritableServiceBase,
  PushOutcome,
} from '@omni/services/offline/offline.service';
import { DateTimeFormatsService } from '@omni/services/date-time-formats/date-time-formats.service';
import { DeviceService } from '@omni/services/device/device.service';
import { DiskService } from '@omni/services/disk/disk.service';
import { DynamicsClientService } from '@omni/data-services/dynamics-client/dynamics-client.service';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

const OFFLINE_ID_PREFIX = 'offline_app_ui_record';

/**
 * A server fault is rolled back and deterministic — a write-back mismatch comes back the same
 * on every attempt — so after this many in a row the record stops retrying and waits for support.
 */
export const MAX_UPLOAD_FAULTS = 3;

/** SaveOrder's refusal of any change to an order that has left Active, a submitted one included. */
export const ORDER_NOT_EDITABLE = 'ORDER_NOT_EDITABLE';

function readRefusals(result: unknown): AppUiRefusal[] {
  if (typeof result !== 'string' || !result.trim()) return [];
  try {
    const parsed = JSON.parse(result);
    const entries = Array.isArray(parsed) ? parsed : [parsed];
    return entries
      .filter(
        (entry): entry is Record<string, unknown> =>
          !!entry && typeof entry === 'object',
      )
      .map((entry) => {
        const keys = Object.fromEntries(
          Object.entries(entry).filter(
            ([name, value]) =>
              /Key$/.test(name) && typeof value === 'string' && value !== '',
          ),
        ) as Record<string, string>;
        return {
          code: String(entry.code ?? ''),
          message: String(entry.message ?? ''),
          ...(entry.attribute ? { attribute: String(entry.attribute) } : {}),
          ...(Object.keys(keys).length ? { keys } : {}),
        };
      });
  } catch {
    return [{ code: '', message: result }];
  }
}

/** The statuses SaveOrder answers with once it has written the order. */
export const LANDED_STATUSES = new Set([
  'created',
  'updated',
  'submitted',
  'validated',
]);

/**
 * Reads a submit action's reply: HTTP 200 carries the business outcome — `Status` is `Rejected`
 * when nothing was written, one of `LANDED_STATUSES` when it was — with the record's id and a
 * `Result` array of `{ code, message, attribute?, …Key? }`. Null for any other answer.
 */
export function readUploadOutcome(
  response: unknown,
): AppUiUploadOutcome | null {
  const reply = (response ?? {}) as Record<string, unknown>;
  const status = String(reply.Status ?? reply.status ?? '').toLowerCase();
  // Any other answer, an empty one included, neither confirms nor refuses the write: read as none.
  if (status !== 'rejected' && !LANDED_STATUSES.has(status)) return null;
  const serverId =
    [reply.RecordId, reply.OrderId, reply.Id, reply.recordId, reply.id].find(
      (value): value is string => typeof value === 'string' && value.length > 0,
    ) ?? null;
  return {
    status: status === 'rejected' ? 'refused' : 'landed',
    serverId,
    refusals: readRefusals(reply.Result ?? reply.result),
  };
}

/** The statuses SaveOrder `Mode: "Scrap"` answers with once the order is scrapped. */
const SCRAPPED_STATUSES = new Set(['scrapped', 'canceled', 'cancelled']);

/** A scrap the server ignored (any other status) did not happen, so it reads as refused. */
export const SCRAP_UNAVAILABLE = 'SCRAP_UNAVAILABLE';

export function readScrapOutcome(response: unknown): AppUiUploadOutcome {
  const status = String(
    (response as Record<string, unknown> | null)?.Status ?? '',
  ).toLowerCase();
  const outcome = readUploadOutcome(response);
  if (outcome?.status === 'refused') return outcome;
  if (SCRAPPED_STATUSES.has(status))
    return { status: 'landed', serverId: null, refusals: [] };
  return {
    status: 'refused',
    serverId: outcome?.serverId ?? null,
    refusals: [{ code: SCRAP_UNAVAILABLE, message: '' }],
  };
}

/**
 * A submitted order is closed to changes: a late autosave must not rewrite what was sent, nor take
 * a queued scrap out of the queue.
 */
function closedToWrites(doc: AppUiRecordDoc): boolean {
  return isSubmittedOnServer(doc) || isScrapQueued(doc);
}

/** Submitted, and the server has the submit: the order is closed to changes. */
export function isSubmittedOnServer(doc: AppUiRecordDoc): boolean {
  return !!doc.submitted && doc.syncStatus === 'landed';
}

/** Scrapped on the device and waiting to tell the server: gone for the rep, still queued. */
export function isScrapQueued(doc: AppUiRecordDoc): boolean {
  return !!doc.pendingPushToDynamics && doc.upload?.mode === 'scrap';
}

/** Submitted and not answered yet: waiting on the device, on its way, or its answer lost. */
export function isSubmitPending(doc: AppUiRecordDoc): boolean {
  return (
    (!!doc.pendingPushToDynamics && doc.upload?.mode === 'submit') ||
    !!doc.submitInFlight ||
    !!doc.submitUnanswered
  );
}

/** The server's own words: Dataverse puts a plug-in's message in `innererror`, not `message`. */
function faultMessage(error: unknown): string | null {
  const e = (error ?? {}) as {
    message?: unknown;
    code?: unknown;
    innererror?: { message?: unknown };
  };
  const text = [e.innererror?.message, e.message].find(
    (m): m is string => typeof m === 'string' && m.trim().length > 0,
  );
  if (!text) return null;
  return typeof e.code === 'string' && e.code ? `${text} (${e.code})` : text;
}

/**
 * The rep edited, saved or submitted the record since it was sent: what it holds or would send is
 * newer. Other writes meanwhile (its order number, a sync stamp) change neither, and a landed reply
 * must still settle it, or it stays queued with a request that already landed and nothing sends it.
 */
function changedSince(current: AppUiRecordDoc, sent: AppUiRecordDoc): boolean {
  return (
    current.modifiedOn !== sent.modifiedOn ||
    JSON.stringify(current.upload ?? null) !==
      JSON.stringify(sent.upload ?? null)
  );
}

/**
 * The stored document as the app reads it; empty when the store holds none. The one cast from the
 * store's untyped documents: every record under this key was written by this service.
 */
function fromStore(
  existing: Record<string, unknown> | undefined,
): AppUiRecordDoc {
  return OfflineServiceBase.unsanitizeFromPouchDB(
    (existing ?? {}) as unknown as AppUiRecordDoc,
  );
}

/** Statuses that say "not now" (sign-in, throttling, a gateway), not "never": retried like no reply. */
const TRANSIENT_STATUSES = new Set([401, 408, 425, 429, 502, 503, 504]);

/** A reply from the server (HTTP 4xx/5xx) that would come back the same, as opposed to no reply at all. */
function isServerFault(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  return (
    typeof status === 'number' &&
    status >= 400 &&
    !TRANSIENT_STATUSES.has(status)
  );
}

/** How one record's send ended: what its own request got back, whatever the record became since. */
export interface AppUiPushOutcome extends PushOutcome {
  status: AppUiUploadOutcome['status'];
  /** The first refusal's message, when refused. */
  reason: string | null;
}

/** One request's answer, for whoever must tell the rep about it. */
export interface AppUiSettled {
  /** The record as it went out. */
  record: AppUiRecordDoc;
  mode: AppUiUploadRequest['mode'];
  status: AppUiUploadOutcome['status'];
  reason: string | null;
  /** A screen waiting on this send reports it itself. */
  reported: boolean;
}

/**
 * True when the server may hold this record: it landed, or a send went out with no reply. A queued
 * record counts unless it is known never to have gone out (`everSent` false).
 */
export function mayHaveReachedServer(doc: AppUiRecordDoc): boolean {
  return (
    !!doc.serverId ||
    doc.acknowledged != null ||
    !!doc.submitted ||
    !!doc.submitUnanswered ||
    !!doc.everSent ||
    (!!doc.pendingPushToDynamics && doc.everSent !== false) ||
    doc.syncStatus === 'landed' ||
    doc.syncStatus === 'unknown'
  );
}

/** `sent` added once: a retry of the same request goes out with the same `acknowledges`. */
function withSent(list: unknown[] | undefined, sent: unknown): unknown[] {
  const known = list ?? [];
  if (sent === undefined) return known;
  const last = known[known.length - 1];
  return JSON.stringify(last) === JSON.stringify(sent)
    ? known
    : [...known, sent];
}

/** True while the device holds something the server does not: queued, refused, or edited since it last landed. */
export function hasUnsavedChanges(doc: AppUiRecordDoc): boolean {
  return (
    doc.syncStatus !== 'landed' ||
    (!!doc.syncedOn && doc.modifiedOn > doc.syncedOn)
  );
}

function syncedOnAfter(
  status: AppUiUploadOutcome['status'],
  previous: AppUiRecordDoc | null,
): string | null {
  return status === 'landed'
    ? new Date().toISOString()
    : (previous?.syncedOn ?? null);
}

/** The record as an upload reply leaves it; `previous` is null when nothing was on disk. */
function settledRecord(
  previous: AppUiRecordDoc | null,
  sent: AppUiRecordDoc,
  outcome: AppUiUploadOutcome,
): AppUiRecordDoc {
  // What landed is on the server whatever happened to the record since; the next request builds on it.
  const acknowledged =
    outcome.status === 'landed' && sent.upload?.acknowledges !== undefined
      ? sent.upload.acknowledges
      : previous?.acknowledged;
  // An earlier submit got no reply and the order is now closed to changes: that submit landed.
  const submittedEarlier =
    outcome.status === 'refused' &&
    previous?.submitUnanswered === true &&
    outcome.refusals.some((refusal) => refusal.code === ORDER_NOT_EDITABLE);
  // A landed submit wins over edits made meanwhile: the server no longer accepts changes to it.
  const submitLanded =
    submittedEarlier ||
    (outcome.status === 'landed' && sent.upload?.mode === 'submit');
  // Any reply settles the doubt: the order is either still open or now known to be submitted.
  if (previous && changedSince(previous, sent) && !submitLanded) {
    return {
      ...previous,
      acknowledged,
      submitUnanswered: false,
      submitInFlight: false,
      serverId: outcome.serverId ?? previous.serverId ?? null,
    } as AppUiRecordDoc;
  }
  const settled = submittedEarlier
    ? { status: 'landed' as const, refusals: [] }
    : outcome;
  const reason = settled.refusals[0];
  // The order now holds what was submitted; edits made meanwhile can no longer reach it.
  const submittedContent =
    submitLanded &&
    !submittedEarlier &&
    previous &&
    changedSince(previous, sent)
      ? {
          state: sent.state,
          values: sent.values,
          summary: sent.summary,
          asOf: sent.asOf,
          modifiedOn: sent.modifiedOn,
        }
      : {};
  return {
    ...previous,
    ...submittedContent,
    submitted:
      submitLanded ||
      (outcome.status === 'landed' ? false : previous?.submitted),
    submitUnanswered: false,
    submitInFlight: false,
    syncStatus: settled.status,
    syncCode: reason?.code || null,
    syncMessage: reason?.message || null,
    syncRefusals: settled.refusals,
    acknowledged,
    serverId: outcome.serverId ?? previous?.serverId ?? null,
    syncedOn: syncedOnAfter(settled.status, previous),
    // Requests go out one at a time, so once one lands as sent the server holds exactly that.
    sentAcknowledges:
      settled.status === 'landed' ? [] : previous?.sentAcknowledges,
    uploadFaults: 0,
    upload: null,
    pendingPushToDynamics: false,
  } as AppUiRecordDoc;
}

/** What queuing a request sets on a record. */
function queuedFields(
  previous: Partial<AppUiRecordDoc> | null,
  request: AppUiUploadRequest,
): Partial<AppUiRecordDoc> {
  return {
    upload: request,
    syncStatus: 'pending',
    syncCode: null,
    syncMessage: null,
    syncRefusals: [],
    uploadFaults: 0,
    pendingPushToDynamics: true,
    // False, not absent: a record queued before this field existed may have gone out.
    everSent: previous?.everSent ?? false,
  };
}

/**
 * Records captured on configured screens, one PouchDB doc each, for every entity. Autosave,
 * Save and Submit all write through `saveAndQueue`: autosave passes no request, so the doc stays
 * out of the upload cycle, except with `AUTO_SEND` on, when it carries a draft request. `queue`
 * attaches a request to the record as stored, e.g. an offline scrap. The global upload cycle
 * drains what is queued through `pushOfflineData`. `saveLocal` is `saveAndQueue` with no request.
 */
@Injectable({ providedIn: 'root' })
export class AppUiRecordDataService extends OfflineWritableServiceBase<
  AppUiRecordDoc,
  'localId'
> {
  public readonly records$: Observable<AppUiRecordDoc[]> = this.raw$;
  /** A record was written on the device or a sync settled; read what you need with `get`. */
  public readonly changed$: Observable<void> = this.dataChanged$;
  protected readonly offlineIdPrefix = OFFLINE_ID_PREFIX;
  protected override readonly offlineDataCountKey =
    OFFLINE_DATA_COUNT_ENTITY_NAME.APP_UI_RECORDS;

  /** The drain in progress, so the upload cycle and master sync never send a record twice at once. */
  private draining: Promise<AppUiPushOutcome[]> | null = null;
  /** A record was queued while a drain ran; that drain read its list before it existed. */
  private drainAgain = false;
  private readonly writes = new Map<string, Promise<unknown>>();
  private readonly settledSubject = new Subject<AppUiSettled>();
  public readonly settled$: Observable<AppUiSettled> =
    this.settledSubject.asObservable();
  /** Records whose send a caller is waiting on, so nothing else announces them. */
  private readonly reportedByCaller = new Map<string, number>();
  /** Records a screen shows now, by local id (or server id for one the device does not keep). */
  private readonly onScreen = new Map<string, number>();

  constructor(
    disk: DiskService,
    deltaService: DeltaService,
    dateTimeFormatsService: DateTimeFormatsService,
    private readonly dynamics: DynamicsClientService,
    private readonly deviceService: DeviceService,
  ) {
    super(
      disk,
      deltaService,
      EntityNames.appUiRecord,
      'localId',
      DB_KEY_PREFIXES.APP_UI_RECORD,
      dateTimeFormatsService,
    );
  }

  /** Same `<prefix>_<rand36>_<timestamp>` shape as every other offline id in the app. */
  public newLocalId(): string {
    return `${OFFLINE_ID_PREFIX}_${Math.random().toString(36).substring(2, 8)}_${Date.now()}`;
  }

  /**
   * Writes the record as it is now. A record edited after the rep saved or submitted leaves the
   * queue: the request on it was built from figures that no longer hold, so it goes back to a
   * draft and the rep saves again. A landed or refused record keeps that status and its server id.
   */
  public async saveLocal(input: AppUiRecordSaveInput): Promise<AppUiRecordDoc> {
    return this.saveAndQueue(input.localId, () => ({ input, request: null }));
  }

  /**
   * As `saveLocal`, built inside the write against the record as stored; a request the build
   * returns is queued by the same write, so no other write to the record comes between the two.
   */
  public async saveAndQueue(
    localId: string,
    write: AppUiRecordWrite,
  ): Promise<AppUiRecordDoc> {
    const now = new Date().toISOString();
    let saved = null as AppUiRecordDoc | null;
    let countChanged = false;
    await this.writeRecord(localId, (existing?: Record<string, unknown>) => {
      const previous = existing?.localId ? fromStore(existing) : null;
      if (previous && closedToWrites(previous)) {
        saved = previous;
        return existing;
      }
      const { input, request } = write(previous);
      const wasPending = previous?.pendingPushToDynamics === true;
      countChanged = wasPending !== !!request;
      saved = {
        ...previous,
        ...input,
        createdOn: previous?.createdOn ?? now,
        modifiedOn: now,
        ...(request
          ? queuedFields(previous, request)
          : {
              syncStatus: wasPending
                ? 'draft'
                : (previous?.syncStatus ?? 'draft'),
              upload: wasPending ? null : (previous?.upload ?? null),
              pendingPushToDynamics: false,
            }),
      } as AppUiRecordDoc;
      return OfflineServiceBase.sanitizeForPouchDB(saved);
    });
    if (countChanged) await this.refreshPendingCount();
    this.notifyLocalChange();
    return saved as AppUiRecordDoc;
  }

  /**
   * Takes the server's copy of a record as the device's: landed, nothing queued, `acknowledged` as
   * the next request's baseline. Over an existing record only when it has nothing unsent.
   */
  public async adoptServerCopy(
    input: AppUiRecordSaveInput & {
      serverId: string;
      serverModifiedOn: string;
      serverName?: string | null;
      acknowledged: unknown;
    },
    /** Replace even unsaved changes: the rep chose the server's version. */
    force = false,
  ): Promise<boolean> {
    let adopted = false;
    await this.writeRecord(
      input.localId,
      (existing?: Record<string, unknown>) => {
        // `upsert` hands a missing doc over as `{}`; only a doc with an id is a record.
        const previous = existing?.localId ? fromStore(existing) : null;
        if (previous && !force && hasUnsavedChanges(previous)) return existing;
        adopted = true;
        return OfflineServiceBase.sanitizeForPouchDB({
          ...previous,
          ...input,
          createdOn: previous?.createdOn ?? input.serverModifiedOn,
          modifiedOn: input.serverModifiedOn,
          syncedOn: input.serverModifiedOn,
          fromServer: previous?.fromServer ?? !previous,
          serverChangedOn: null,
          serverChangedTotal: null,
          sentAcknowledges: [],
          syncStatus: 'landed',
          syncCode: null,
          syncMessage: null,
          syncRefusals: [],
          submitted: false,
          submitUnanswered: false,
          upload: null,
          pendingPushToDynamics: false,
        } as AppUiRecordDoc);
      },
    );
    if (adopted) this.notifyLocalChange();
    return adopted;
  }

  /** Records which server version a landed copy matches, so the next pull can tell it is current. */
  public async markServerVersion(
    localId: string,
    serverModifiedOn: string,
    serverName?: string | null,
  ): Promise<void> {
    await this.patch(localId, {
      serverModifiedOn,
      ...(serverName ? { serverName } : {}),
    });
  }

  /** What the server calls the record, learnt as soon as it holds it rather than at the next pull. */
  public async setServerName(
    localId: string,
    serverName: string,
  ): Promise<void> {
    await this.patch(localId, { serverName });
  }

  /** Another device saved the record while this one holds unsaved changes; opening it asks which to keep. */
  public async flagServerChange(
    localId: string,
    change: { modifiedOn: string; total: string; serverName?: string | null },
  ): Promise<void> {
    await this.patch(localId, {
      serverChangedOn: change.modifiedOn,
      serverChangedTotal: change.total,
      ...(change.serverName ? { serverName: change.serverName } : {}),
    });
  }

  /** The rep keeps this device's changes: the server's version is acknowledged and Save overwrites it. */
  public async keepDeviceCopy(localId: string): Promise<void> {
    const doc = await this.get(localId);
    if (!doc?.serverChangedOn) return;
    await this.patch(localId, {
      serverModifiedOn: doc.serverChangedOn,
      serverChangedOn: null,
      serverChangedTotal: null,
    });
  }

  /** Marks landed records the server no longer holds open as submitted, which takes them off the drafts list. */
  public async closeLanded(localIds: readonly string[]): Promise<void> {
    for (const localId of localIds) {
      if ((await this.get(localId))?.syncStatus !== 'landed') continue;
      await this.writeRecord(localId, (existing?: Record<string, unknown>) =>
        OfflineServiceBase.sanitizeForPouchDB({
          ...fromStore(existing),
          submitted: true,
        } as AppUiRecordDoc),
      );
    }
    if (localIds.length) this.notifyLocalChange();
  }

  /** Attaches what to send and puts the record in the upload queue. */
  public async queue(
    localId: string,
    request: AppUiUploadRequest,
  ): Promise<void> {
    // Patched inside the write, as `saveLocal` and `settle` do: a settle landing between a read
    // and this write would otherwise lose the server id and what it acknowledged.
    let found = false;
    await this.writeRecord(localId, (existing?: Record<string, unknown>) => {
      found = !!existing;
      const previous: Partial<AppUiRecordDoc> = existing
        ? fromStore(existing)
        : {};
      return OfflineServiceBase.sanitizeForPouchDB({
        ...previous,
        ...queuedFields(previous, request),
      } as AppUiRecordDoc);
    });
    if (!found)
      throw new Error(
        `AppUiRecordDataService: cannot queue missing record ${localId}`,
      );
    await this.refreshPendingCount();
    this.notifyLocalChange();
  }

  public async get(localId: string): Promise<AppUiRecordDoc | null> {
    const doc = await this.disk.retrieve(this.docKey(localId), true);
    return doc
      ? OfflineServiceBase.unsanitizeFromPouchDB(doc as AppUiRecordDoc)
      : null;
  }

  /** Calls an action now, outside the upload queue, and returns its reply. Throws as the call does. */
  public async executeNow(
    action: string,
    params: Record<string, unknown>,
  ): Promise<unknown> {
    return this.dynamics.executeUnboundAction(action, params);
  }

  /**
   * Every record, read from disk now. Not from `records$`: while anything subscribes to it, it
   * replays the last read, which can predate a write a moment ago — the drain would then miss a
   * record queued by the Save that started it.
   */
  public async list(): Promise<AppUiRecordDoc[]> {
    const docs = await this.disk
      .find<AppUiRecordDoc>({
        selector: {
          _id: {
            $gte: `${this.dbKeyPrefix}_`,
            $lte: `${this.dbKeyPrefix}_${PREFIX_SEARCH_ENDKEY_UNICODE}`,
          },
        },
      })
      .catch(() => [] as AppUiRecordDoc[]);
    return (docs ?? []).map((doc) =>
      OfflineServiceBase.unsanitizeFromPouchDB(doc),
    );
  }

  /**
   * Drains the queue: one action call per record, the reply written onto the record. Done here
   * rather than through the base class's push, because a record keeps its local key after it
   * lands (the server id is a field, not the key) and a refusal settles the record too — the
   * base would keep it pending. A call that gets no reply leaves the record pending for the next
   * cycle; that is safe because every id in the request was minted on the device. A server fault
   * counts towards `MAX_UPLOAD_FAULTS`. Returns what settled, as the base does; the local key
   * never changes, so `serverId` is informational.
   */
  public override async pushOfflineData(): Promise<AppUiPushOutcome[]> {
    if (!(await Promise.resolve(this.isSyncAllowed()))) return [];
    if (this.draining) {
      this.drainAgain = true;
      const joining = this.draining;
      const joined = await joining;
      // The drain had already made its last pass when this call joined it, so nothing sent this
      // caller's record: drain again rather than leave it for the next sync.
      return this.drainAgain && this.draining !== joining
        ? [...joined, ...(await this.pushOfflineData())]
        : joined;
    }
    this.draining = (async () => {
      const settled: AppUiPushOutcome[] = [];
      do {
        this.drainAgain = false;
        settled.push(...(await this.drain()));
      } while (this.drainAgain);
      return settled;
    })().finally(() => (this.draining = null));
    return this.draining;
  }

  /** Claims a record for a screen showing it; returns the release. A pull leaves such a record as it is. */
  public showOnScreen(key: string): () => void {
    this.onScreen.set(key, (this.onScreen.get(key) ?? 0) + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      const left = (this.onScreen.get(key) ?? 1) - 1;
      if (left > 0) this.onScreen.set(key, left);
      else this.onScreen.delete(key);
    };
  }

  public isOnScreen(key: string): boolean {
    return this.onScreen.has(key);
  }

  /** Marks a record whose send the caller will announce; returns the release. */
  public reportFor(localId: string): () => void {
    this.reportedByCaller.set(
      localId,
      (this.reportedByCaller.get(localId) ?? 0) + 1,
    );
    return () => {
      const left = (this.reportedByCaller.get(localId) ?? 1) - 1;
      if (left > 0) this.reportedByCaller.set(localId, left);
      else this.reportedByCaller.delete(localId);
    };
  }

  /** Behind any write to the record still in progress, so a late write cannot bring it back. */
  public override async removeLocal(localId: string): Promise<void> {
    await this.serial(localId, async () => super.removeLocal(localId));
  }

  /**
   * Removes a record only while the server cannot hold it: never sent, so nothing can be left there.
   * Unqueued in the same write that checks, so a send cannot start in between. True when removed.
   */
  public async discardIfUnsent(localId: string): Promise<boolean> {
    if (!(await this.exists(localId))) return true;
    let unsent = false;
    await this.writeRecord(localId, (existing?: Record<string, unknown>) => {
      const previous = fromStore(existing);
      unsent = !mayHaveReachedServer(previous);
      return unsent
        ? OfflineServiceBase.sanitizeForPouchDB({
            ...previous,
            upload: null,
            pendingPushToDynamics: false,
          } as AppUiRecordDoc)
        : existing;
    });
    if (unsent) await this.removeLocal(localId);
    return unsent;
  }

  /**
   * After the app starts: the pending count is rebuilt, and what was queued when the app went away
   * (closed, or killed mid-send) goes now rather than at the next sync. Not awaited: no network on
   * the start-up path.
   */
  public async resumeQueue(): Promise<void> {
    const pending = await this.refreshPendingCount();
    if (pending > 0)
      void this.pushOfflineData().catch((error) =>
        console.error(
          'AppUiRecordDataService: resuming the queue failed',
          error,
        ),
      );
  }

  /** Nothing goes out offline, a rep who chose to work offline included, so nothing is marked as sent. */
  protected override isSyncAllowed(): boolean {
    return !this.deviceService.isOffline;
  }

  protected async fetchData(): Promise<AppUiRecordDoc[]> {
    // Device-captured records are never pulled; what the server holds comes back through
    // each entity's own sync.
    return [];
  }

  protected async pushData(_pending: AppUiRecordDoc[]): Promise<PushOutcome[]> {
    // Unused: `pushOfflineData` is overridden above, and nothing calls `sync()` on this service.
    return [];
  }

  private async refreshPendingCount(): Promise<number> {
    const pending = (await this.list()).filter(
      (doc) => doc.pendingPushToDynamics === true,
    ).length;
    this.disk.setOfflineDataCount(
      OFFLINE_DATA_COUNT_ENTITY_NAME.APP_UI_RECORDS,
      pending,
    );
    return pending;
  }

  private async drain(): Promise<AppUiPushOutcome[]> {
    const pending = (await this.list()).filter(
      (doc) => doc.pendingPushToDynamics === true && doc.upload,
    );
    const settled: AppUiPushOutcome[] = [];
    for (const listed of pending) {
      // The connection went during the pass: what is left waits, unmarked, for the next one.
      if (this.deviceService.isOffline) break;
      const outcome = await this.send(listed.localId);
      if (!outcome) continue;
      settled.push(outcome);
      // A screen showing this record says so now: the rest of the pass may take a while.
      this.notifyLocalChange();
    }
    // Recounted rather than taken from this pass: a record queued while it ran must still count.
    await this.refreshPendingCount();
    this.notifyLocalChange();
    return settled;
  }

  /** One record's call, its answer written onto it; null when it was not sent or got no answer. */
  private async send(localId: string): Promise<AppUiPushOutcome | null> {
    const doc = await this.markSending(localId);
    if (!doc) return null;
    const request = doc.upload as AppUiUploadRequest;
    let response: unknown;
    try {
      response = await this.dynamics.executeUnboundAction(
        request.action,
        request.params,
      );
    } catch (error) {
      console.error(
        `AppUiRecordDataService: ${request.action} failed for ${doc.localId}`,
        error,
      );
      if (isServerFault(error)) await this.countFault(doc, error);
      else if (request.mode === 'submit') await this.markSubmitUnanswered(doc);
      return null;
    }
    const outcome = await this.answer(doc, request, response);
    if (!outcome) {
      console.error(
        `AppUiRecordDataService: ${request.action} answered with no known status for ${doc.localId}`,
        response,
      );
      if (request.mode === 'submit') await this.markSubmitUnanswered(doc);
      return null;
    }
    const reason = outcome.refusals[0]?.message ?? null;
    this.settledSubject.next({
      record: doc,
      mode: request.mode,
      status: outcome.status,
      reason,
      reported: this.reportedByCaller.has(doc.localId),
    });
    return {
      localId: doc.localId,
      serverId: outcome.serverId ?? doc.localId,
      status: outcome.status,
      reason,
    };
  }

  /** Writes the reply. A scrapped record has nothing left to keep; a refused scrap comes back with why. */
  private async answer(
    doc: AppUiRecordDoc,
    request: AppUiUploadRequest,
    response: unknown,
  ): Promise<AppUiUploadOutcome | null> {
    if (request.mode !== 'scrap') {
      const outcome = readUploadOutcome(response);
      if (outcome) await this.settle(doc, outcome);
      return outcome;
    }
    const outcome = readScrapOutcome(response);
    if (outcome.status === 'landed') await this.removeLocal(doc.localId);
    else await this.settle(doc, outcome);
    return outcome;
  }

  /**
   * Writes the reply onto the record. When the record changed while the call was in flight — the
   * rep edited it, or saved or submitted again — only the server id is kept: the newer state is
   * what the next cycle must send, and settling it would drop it.
   */
  private async settle(
    sent: AppUiRecordDoc,
    outcome: AppUiUploadOutcome,
  ): Promise<void> {
    // A record the rep discarded while it was in flight stays gone.
    if (!(await this.exists(sent.localId))) return;
    await this.writeRecord(
      sent.localId,
      (existing?: Record<string, unknown>) => {
        const previous = existing ? fromStore(existing) : null;
        return OfflineServiceBase.sanitizeForPouchDB(
          settledRecord(previous, sent, outcome),
        );
      },
    );
  }

  /**
   * The record as it goes out now, which may be newer than the pass listed. Marked in the same
   * write that reads it, so neither a lost reply nor the app going away can hide that the server may
   * hold it, nor what it may hold. Null once the record is no longer queued.
   */
  private async markSending(localId: string): Promise<AppUiRecordDoc | null> {
    if (!(await this.exists(localId))) return null;
    let marked = null as AppUiRecordDoc | null;
    const rev = await this.writeRecord(
      localId,
      (existing?: Record<string, unknown>) => {
        const current = fromStore(existing);
        marked = null;
        if (!current.pendingPushToDynamics || !current.upload) return existing;
        const submit = current.upload.mode === 'submit';
        marked = {
          ...current,
          everSent: true,
          sentAcknowledges: withSent(
            current.sentAcknowledges,
            current.upload.acknowledges,
          ),
          ...(submit
            ? {
                submitInFlight: true,
                ...(current.submitInFlight ? { submitUnanswered: true } : {}),
              }
            : {}),
        };
        return OfflineServiceBase.sanitizeForPouchDB(marked);
      },
    );
    if (!marked || !rev) return null;
    return { ...marked, _rev: rev } as AppUiRecordDoc;
  }

  /** A submit that got no reply may still have landed; a later refusal is read in that light. */
  private async markSubmitUnanswered(sent: AppUiRecordDoc): Promise<void> {
    if (!(await this.exists(sent.localId))) return;
    await this.writeRecord(
      sent.localId,
      (existing?: Record<string, unknown>) => {
        const previous = fromStore(existing);
        return OfflineServiceBase.sanitizeForPouchDB({
          ...previous,
          submitUnanswered: true,
        } as AppUiRecordDoc);
      },
    );
  }

  /**
   * Counts a server fault; at the cap the record leaves the queue as `unknown`. Returns true then.
   * A record changed since it was sent carries a new request, which has not faulted yet.
   */
  private async countFault(
    sent: AppUiRecordDoc,
    error: unknown,
  ): Promise<boolean> {
    if (!(await this.exists(sent.localId))) return true;
    let gaveUp = false;
    await this.writeRecord(
      sent.localId,
      (existing?: Record<string, unknown>) => {
        const previous = fromStore(existing);
        if (changedSince(previous, sent)) return existing;
        const faults = (previous.uploadFaults ?? 0) + 1;
        gaveUp = faults >= MAX_UPLOAD_FAULTS;
        return OfflineServiceBase.sanitizeForPouchDB({
          ...previous,
          uploadFaults: faults,
          // Order state is checked before anything is written, so a fault means it was still open.
          submitUnanswered: false,
          submitInFlight: false,
          syncMessage: faultMessage(error) ?? previous.syncMessage ?? null,
          ...(gaveUp
            ? { syncStatus: 'unknown', pendingPushToDynamics: false }
            : {}),
        } as AppUiRecordDoc);
      },
    );
    return gaveUp;
  }

  private async patch(
    localId: string,
    fields: Partial<AppUiRecordDoc>,
  ): Promise<void> {
    if (!(await this.exists(localId))) return;
    await this.writeRecord(localId, (existing?: Record<string, unknown>) =>
      OfflineServiceBase.sanitizeForPouchDB({
        ...fromStore(existing),
        ...fields,
      } as AppUiRecordDoc),
    );
    this.notifyLocalChange();
  }

  /** One write at a time per record: when the disk encrypts, it does not retry a conflicting put. */
  private async writeRecord(
    localId: string,
    change: (existing?: Record<string, unknown>) => unknown,
  ): Promise<string | null> {
    const result = (await this.serial(localId, () =>
      this.disk.updateOrInsert(this.docKey(localId), change),
    )) as { rev?: string } | undefined;
    // The disk reports a failed write (storage full, encryption) by answering nothing.
    if (!result)
      throw new Error(`AppUiRecordDataService: ${localId} was not written`);
    return result.rev ?? null;
  }

  private async serial<T>(localId: string, work: () => Promise<T>): Promise<T> {
    const run = (this.writes.get(localId) ?? Promise.resolve()).then(work);
    const done = run.catch(() => undefined);
    this.writes.set(localId, done);
    try {
      return await run;
    } finally {
      if (this.writes.get(localId) === done) this.writes.delete(localId);
    }
  }

  private async exists(localId: string): Promise<boolean> {
    return !!(await this.disk
      .retrieve(this.docKey(localId), true)
      .catch(() => null));
  }

  private docKey(localId: string): string {
    return `${this.dbKeyPrefix}_${localId}`;
  }
}
